import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { MetricPoint } from '../api.js';
import type { Delta } from '../lib/format.js';
import { fmtNumber } from '../lib/format.js';
import { relativeAgo, isStale } from '../lib/freshness.js';
import Sparkline from './Sparkline.js';

export interface StatTileProps {
  label: string;
  /** Primary readout; a string is shown verbatim, a number is formatted. */
  value: string | number | null | undefined;
  unit?: string;
  accent: string;
  source?: string;
  spark?: MetricPoint[];
  delta?: Delta | null;
  /** Caption under the delta, e.g. "vs 7d ago". */
  deltaCaption?: string;
  note?: ReactNode;
  /** Deviation-from-baseline word in the foot; coexists with delta/note. */
  deviation?: { label: string; tone: 'good' | 'neutral' | 'warn' };
  /** ISO timestamp of the underlying reading; renders an "as-of" freshness label. */
  asOf?: string;
  /** Hours after which the as-of label turns amber. Defaults to 36. */
  staleAfterHours?: number;
  loading?: boolean;
  error?: boolean;
  /** Makes the whole tile a link (drill-down to the metric). */
  to?: string;
}

function deltaArrow(dir: Delta['dir']): string {
  return dir === 'up' ? '↑' : dir === 'down' ? '↓' : '→';
}

/**
 * One metric, Apple Health summary style: coloured label, one big number,
 * a quiet foot (delta or note) and a tiny trend. The number is the headline;
 * everything else is secondary.
 */
export default function StatTile(props: StatTileProps): JSX.Element {
  const { label, value, unit, accent, source, spark, delta, deltaCaption, note, deviation, asOf, staleAfterHours, loading, error, to } =
    props;

  const stale = asOf ? isStale(asOf, staleAfterHours ?? 36) : false;
  const shown =
    value === null || value === undefined || value === '' ? '—' : typeof value === 'number' ? fmtNumber(value) : value;

  const body = (
    <>
      <div className="hb-tile__head">
        <span className="hb-tile__label" title={source ? `${label} · ${source}` : label}>
          {label}
        </span>
        {asOf && <span className={'hb-tile__asof' + (stale ? ' hb-tile__asof--stale' : '')}>{relativeAgo(asOf)}</span>}
      </div>

      {loading ? (
        <div className="hb-skel" aria-label="Loading" />
      ) : (
        <div className="hb-tile__readout">
          <span className={`hb-tile__value${error || shown === '—' ? ' hb-tile__value--muted' : ''}`}>{error ? '—' : shown}</span>
          {unit && !error && shown !== '—' && <span className="hb-tile__unit">{unit}</span>}
        </div>
      )}

      <div className="hb-tile__foot">
        <span className="hb-tile__note">
          {delta ? (
            <span className="hb-delta">
              {deltaArrow(delta.dir)} {fmtNumber(Math.abs(delta.value))}
              {deltaCaption && ` ${deltaCaption}`}
            </span>
          ) : note ? (
            note
          ) : null}
          {deviation && (
            <>
              {(delta || note) && ' · '}
              <span className={`hb-dev hb-dev--${deviation.tone}`}>{deviation.label}</span>
            </>
          )}
        </span>
        {spark && spark.length > 1 && !error && (
          <span className="hb-tile__spark">
            <Sparkline points={spark} color={accent} width={64} height={24} />
          </span>
        )}
      </div>
    </>
  );

  const style = { ['--tile-accent' as string]: accent };
  return to ? (
    <Link to={to} className="hb-tile" style={style}>
      {body}
    </Link>
  ) : (
    <div className="hb-tile" style={style}>
      {body}
    </div>
  );
}
